import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import qrCode from 'qrcode';

import { InputElementMetaUiFeatures } from '@blaze-shared/elements-domain';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { CanvasElementBaseComponent } from '../__base/base.component';

export const DEFAULT_URL = 'https://blaze.tech';

// TODO: test
@UntilDestroy()
@Component({
  selector: 'be-qrcode',
  templateUrl: './qr-code.component.html',
  styleUrls: ['./qr-code.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    '[style.--b-qr-logo-color]': 'iconColor()',
    '[style.--b-qr-logo-backgroundColor]': 'iconBackgroundColor()',
  },
})
export class QrCodeComponent
  extends CanvasElementBaseComponent<'qrcode'>
  implements CanvasElementComponent<'qrcode'>, OnInit, OnChanges
{
  @Input() value?: string;
  @ViewChild('qrImage', { static: true }) private qrImage!: ElementRef<HTMLImageElement>;

  iconBackgroundColor = signal('#ffffff');
  iconColor = signal('var(--bat-accent)');
  labelEnabled = signal(false);
  labelText = signal('');
  qrCodeStyles = signal<Record<string, string>>({});
  styles = signal<Record<string, string>>({});

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes?.['props'] || changes?.['value']) {
      this.renderQrCode();

      const { title } = this.props?.content || {};
      this.labelEnabled.set(title?.enabled ?? true);

      this.updateStyles();
    }
  }

  async ngOnInit(): Promise<void> {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText.set((features.title ?? '') + this.getRequiredSuffix(features));
      });

    this.renderQrCode();
  }

  isEmptyUrl(): boolean {
    return this.props?.content?.url?.type === 'add' && !this.props?.content.url?.value;
  }

  private async renderQrCode(): Promise<void> {
    const image = this.qrImage?.nativeElement;
    if (!image) {
      return;
    }

    const url = await this.getUrl();
    if (!url) {
      // Nothing to draw — must still unblock the Print to PDF readiness signal.
      image.removeAttribute('data-print-pending');
      return;
    }

    try {
      image.src = await qrCode.toDataURL(url, {
        width: 2000,
        margin: 0,
        color: {
          dark: this.props?.style.code?.foreground.color1,
          light: this.props?.style.code?.background.color1,
        },
      });
      // Wait until the bitmap is actually ready to paint before unblocking the
      // Print to PDF readiness marker — setting src alone is not enough.
      await image.decode();
    } catch (err) {
      console.error(err);
    } finally {
      image.removeAttribute('data-print-pending');
    }
  }

  private getUrl(): Promise<string> {
    if (this.props?.content.url?.type === 'current') {
      return new Promise((resolve) => {
        setTimeout(() => resolve(window.location.href));
      });
    } else if (this.props?.content.url?.type === 'dynamic') {
      return Promise.resolve(this.value || '');
    }
    return Promise.resolve(this.props?.content.url?.value || DEFAULT_URL);
  }

  private async updateStyles(): Promise<void> {
    const { style, content } = this.props || {};
    const { title, font } = content || {};
    const { general, labelBlock } = style || {};
    if (!style || !title || !font || !general || !labelBlock) {
      return;
    }
    const url = await this.getUrl();
    const isEmptyUrl = url ? url === DEFAULT_URL : true;

    const accentColor = 'var(--bat-accent)';
    const qrCodeStyles: Record<string, string> = {};

    const styles: Record<string, string> = {};
    if (title.enabled !== false) {
      if (title.position === 'top') {
        styles['flex-direction'] = 'column';
        styles['align-items'] = 'start';
        qrCodeStyles['max-height'] = 'calc(100% - 30px)';
        qrCodeStyles['margin-left'] = '0';
        qrCodeStyles['padding-left'] = '0';
      } else {
        qrCodeStyles['max-width'] = `calc(100% - ${title.width})`;
        qrCodeStyles['height'] = '100%';
      }
    } else {
      qrCodeStyles['margin-right'] = 'auto';
      qrCodeStyles['margin-top'] = 'auto';
      qrCodeStyles['margin-bottom'] = 'auto';
      qrCodeStyles['height'] = '100%';
    }

    if (isEmptyUrl) {
      qrCodeStyles['opacity'] = '0.1';
    }

    this.qrCodeStyles.set(qrCodeStyles);
    this.styles.set(styles);
    this.iconColor.set(style.logo?.color.color1 ?? accentColor);
    this.iconBackgroundColor.set(style.code?.background.color1 as string);
  }
}
